import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { EMPLOYEE_CONFIG } from "../../../../../../constants/employeeConfig";
import { buildMasterlistWorksheet } from "../../../../../../utils/employee/buildWorksheet";
import { fetchDataEmployeeInfoForMasterlist } from "../../../EmployeesListApi";
import { getDynamicLeaveConfig } from "../../../../../../utils/employee/dynamicLeaveHelper";

// =============================================================================
// FORMAT DATE
// Converts ISO date string to MM/DD/YYYY format for Excel display.
// Returns empty string if date is null or invalid.
// =============================================================================
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

// =============================================================================
// RESOLVE MASTERLIST KEY
// The masterlist API response returns display strings alongside IDs.
// e.g. locationId → locationDescription
//      companyId  → companyName          (deviates from Description pattern)
//      shiftScheduleId → shiftScheduleShiftName (deviates)
//
// Fields that deviate have a masterlistKey defined in the config.
// Everything else follows the default pattern: remove "Id", append "Description"
//
// @param field  - resolved field from config
// @param source - the section object from the API response
// =============================================================================
const getMasterlistValue = (field, source) => {
  if (!source) return "";

  // If field has an explicit masterlistKey defined, use it directly
  if (field.masterlistKey) {
    const val = source[field.masterlistKey];
    return field.type === "date" ? formatDate(val) : (val ?? "");
  }

  // If the key ends with "Id", try the Description variant first
  if (field.key.endsWith("Id")) {
    const baseKey = field.key.replace("Id", "");
    const descriptionKey = `${baseKey}Description`;
    if (source[descriptionKey] !== undefined) {
      return source[descriptionKey] ?? "";
    }
  }

  // For date fields, format the value
  if (field.type === "date") {
    return formatDate(source[field.key]);
  }

  // Default — read directly from source
  return source[field.key] ?? "";
};

// =============================================================================
// PREPEND FIELDS
// All masterlist sheets need EmployeeId and EmployeeName as the first columns.
// =============================================================================
const MASTERLIST_PREPEND_FIELDS = [
  { key: "employeeId", label: "EmployeeId", width: 30, masterlistKey: "employeeId" },
  { key: "fullName",   label: "EmployeeName", width: 30, masterlistKey: "fullName" },
];

// =============================================================================
// GENERATE DOWNLOAD MASTERLIST
// Generates a full employee masterlist Excel file with all sections.
// Each sheet corresponds to a section in EMPLOYEE_CONFIG.
// =============================================================================
export const generateDownloadMasterlist = async (filters, showGeneralMessage) => {
  const _employees = await fetchDataEmployeeInfoForMasterlist(filters);
console.log({_employees});
  if (!_employees || _employees.length <= 0) {
    showGeneralMessage("Please wait for the employees to load", false);
    return;
  }

  const workbook = new ExcelJS.Workbook();

  // -------------------------------------------------------------------------
  // 1. EmployeePersonalInformation
  // Root fields are flat on the employee object — read directly
  // -------------------------------------------------------------------------
  buildMasterlistWorksheet(
    workbook,
    "EmployeePersonalInformation",
    {
      apiKey: null,
      feature: null,
      fields: [
        ...EMPLOYEE_CONFIG.root.fields,
        ...EMPLOYEE_CONFIG.address.fields,
      ],
    },
    _employees,
    {
      getRowValues: (employee, fields) =>
        fields.map((f) => {
          if (f.type === "date") return formatDate(employee[f.key]);
          if (f.type === "file") return ""; // skip binary fields
          return employee[f.key] ?? "";
        }),
    }
  );

  // -------------------------------------------------------------------------
  // 2. EmployeeInformation
  // Uses getMasterlistValue to resolve display strings from nested object
  // -------------------------------------------------------------------------
  buildMasterlistWorksheet(
    workbook,
    "EmployeeInformation",
    EMPLOYEE_CONFIG.employeeInformation,
    _employees,
    {
      prependFields: MASTERLIST_PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        const source = employee.employeeInformation;
        return fields.map((f) => {
          // prependFields read from root employee object
          if (f.masterlistKey) return employee[f.masterlistKey] ?? "";
          return getMasterlistValue(f, source);
        });
      },
    }
  );

  // -------------------------------------------------------------------------
  // 3. AccountInformation
  // -------------------------------------------------------------------------
  buildMasterlistWorksheet(
    workbook,
    "AccountInformation",
    EMPLOYEE_CONFIG.employeeAccountInformation,
    _employees,
    {
      prependFields: MASTERLIST_PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        const source = employee.employeeAccountInformation;
        return fields.map((f) => {
          if (f.masterlistKey) return employee[f.masterlistKey] ?? "";
          return source?.[f.key] ?? "";
        });
      },
    }
  );

  // -------------------------------------------------------------------------
  // 4. PassportDetail
  // -------------------------------------------------------------------------
  buildMasterlistWorksheet(
    workbook,
    "PassportDetail",
    EMPLOYEE_CONFIG.employeePassportDetail,
    _employees,
    {
      prependFields: MASTERLIST_PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        const source = employee.employeePassportDetail;
        return fields.map((f) => {
          if (f.masterlistKey) return employee[f.masterlistKey] ?? "";
          if (!source) return "";
          return f.type === "date" ? formatDate(source[f.key]) : (source[f.key] ?? "");
        });
      },
    }
  );

  // -------------------------------------------------------------------------
  // 5. SeamanBookDetail (feature-flagged — skipped automatically if off)
  // -------------------------------------------------------------------------
  buildMasterlistWorksheet(
    workbook,
    "SeamanBookDetail",
    EMPLOYEE_CONFIG.employeeSeamanBookDetail,
    _employees,
    {
      prependFields: MASTERLIST_PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        const source = employee.employeeSeamanBookDetail;
        return fields.map((f) => {
          if (f.masterlistKey) return employee[f.masterlistKey] ?? "";
          if (!source) return "";
          return f.type === "date" ? formatDate(source[f.key]) : (source[f.key] ?? "");
        });
      },
    }
  );

  // -------------------------------------------------------------------------
  // 6. LicenseInformation (feature-flagged — skipped automatically if off)
  // -------------------------------------------------------------------------
  buildMasterlistWorksheet(
    workbook,
    "LicenseInformation",
    EMPLOYEE_CONFIG.employeeLicenseInformation,
    _employees,
    {
      prependFields: MASTERLIST_PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        const source = employee.employeeLicenseInformation;
        return fields.map((f) => {
          if (f.masterlistKey) return employee[f.masterlistKey] ?? "";
          if (!source) return "";
          if (f.type === "boolean") {
            return source[f.key] === true ? "Yes" : source[f.key] === false ? "No" : "";
          }
          return f.type === "date" ? formatDate(source[f.key]) : (source[f.key] ?? "");
        });
      },
    }
  );

  // -------------------------------------------------------------------------
  // 7. BankAccount — array section, multiple rows per employee
  // -------------------------------------------------------------------------
  buildMasterlistWorksheet(
    workbook,
    "BankAccount",
    EMPLOYEE_CONFIG.employeeBankAccounts,
    _employees,
    {
      prependFields: MASTERLIST_PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        const accounts = employee.employeeBankAccounts;
        if (!accounts || accounts.length === 0) {
          return [fields.map((f) => f.masterlistKey ? (employee[f.masterlistKey] ?? "") : "")];
        }
        // Return multiple rows — one per bank account
        return accounts.map((account) =>
          fields.map((f) => {
            if (f.masterlistKey) return employee[f.masterlistKey] ?? "";
            return account[f.key] ?? "";
          })
        );
      },
    }
  );

  // -------------------------------------------------------------------------
  // 8. EmployeeSalary
  // -------------------------------------------------------------------------
  buildMasterlistWorksheet(
    workbook,
    "EmployeeSalary",
    EMPLOYEE_CONFIG.employeeSalary,
    _employees,
    {
      prependFields: MASTERLIST_PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        const source = employee.employeeSalary;
        return fields.map((f) => {
          if (f.masterlistKey) return employee[f.masterlistKey] ?? "";
          return source?.[f.key] ?? "";
        });
      },
    }
  );

  // -------------------------------------------------------------------------
  // 9. Dependents — array section
  // -------------------------------------------------------------------------
  buildMasterlistWorksheet(
    workbook,
    "Dependents",
    EMPLOYEE_CONFIG.employeeDependents,
    _employees,
    {
      prependFields: MASTERLIST_PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        const items = employee.employeeDependents;
        if (!items || items.length === 0) {
          return [fields.map((f) => f.masterlistKey ? (employee[f.masterlistKey] ?? "") : "")];
        }
        return items.map((item) =>
          fields.map((f) => {
            if (f.masterlistKey) return employee[f.masterlistKey] ?? "";
            return f.type === "date" ? formatDate(item[f.key]) : (item[f.key] ?? "");
          })
        );
      },
    }
  );

  // -------------------------------------------------------------------------
  // 10. EducationalAttainment — array section
  // -------------------------------------------------------------------------
  buildMasterlistWorksheet(
    workbook,
    "EducationalAttainment",
    EMPLOYEE_CONFIG.employeeEducationAttainments,
    _employees,
    {
      prependFields: MASTERLIST_PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        const items = employee.employeeEducationAttainments;
        if (!items || items.length === 0) {
          return [fields.map((f) => f.masterlistKey ? (employee[f.masterlistKey] ?? "") : "")];
        }
        return items.map((item) =>
          fields.map((f) => {
            if (f.masterlistKey) return employee[f.masterlistKey] ?? "";
            return f.type === "date" ? formatDate(item[f.key]) : (item[f.key] ?? "");
          })
        );
      },
    }
  );

  // -------------------------------------------------------------------------
  // 11. TrainingSeminar — array section
  // NOTE: API returns "employeeTrainingsSeminar" (different from config key)
  // -------------------------------------------------------------------------
  buildMasterlistWorksheet(
    workbook,
    "TrainingSeminar",
    EMPLOYEE_CONFIG.employeeTrainingSeminars,
    _employees,
    {
      prependFields: MASTERLIST_PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        // API uses "employeeTrainingsSeminar" not "employeeTrainingSeminars"
        const items = employee.employeeTrainingsSeminar;
        if (!items || items.length === 0) {
          return [fields.map((f) => f.masterlistKey ? (employee[f.masterlistKey] ?? "") : "")];
        }
        return items.map((item) =>
          fields.map((f) => {
            if (f.masterlistKey) return employee[f.masterlistKey] ?? "";
            return f.type === "date" ? formatDate(item[f.key]) : (item[f.key] ?? "");
          })
        );
      },
    }
  );

  // -------------------------------------------------------------------------
  // 12. EmergencyContact — array section
  // -------------------------------------------------------------------------
  buildMasterlistWorksheet(
    workbook,
    "EmergencyContact",
    EMPLOYEE_CONFIG.employeeEmergencyContacts,
    _employees,
    {
      prependFields: MASTERLIST_PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        const items = employee.employeeEmergencyContacts;
        if (!items || items.length === 0) {
          return [fields.map((f) => f.masterlistKey ? (employee[f.masterlistKey] ?? "") : "")];
        }
        return items.map((item) =>
          fields.map((f) => {
            if (f.masterlistKey) return employee[f.masterlistKey] ?? "";
            return item[f.key] ?? "";
          })
        );
      },
    }
  );

  // -------------------------------------------------------------------------
  // 13. FamilyData — array section
  // -------------------------------------------------------------------------
  buildMasterlistWorksheet(
    workbook,
    "FamilyData",
    EMPLOYEE_CONFIG.employeeFamilyData,
    _employees,
    {
      prependFields: MASTERLIST_PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        const items = employee.employeeFamilyData;
        if (!items || items.length === 0) {
          return [fields.map((f) => f.masterlistKey ? (employee[f.masterlistKey] ?? "") : "")];
        }
        return items.map((item) =>
          fields.map((f) => {
            if (f.masterlistKey) return employee[f.masterlistKey] ?? "";
            return f.type === "date" ? formatDate(item[f.key]) : (item[f.key] ?? "");
          })
        );
      },
    }
  );

  // -------------------------------------------------------------------------
  // 14. EmployeeLeaves
  // -------------------------------------------------------------------------
  const dynamicLeaveMasterConfig = await getDynamicLeaveConfig(); // ADDED

  buildMasterlistWorksheet(
    workbook,
    "EmployeeLeaves",
    dynamicLeaveMasterConfig, // REPLACED: EMPLOYEE_CONFIG.employeeLeave
    _employees,
    {
      prependFields: MASTERLIST_PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        // Maps the C# collection based on leaveTypeId
        const balances = employee.employeeLeaveBalances || [];
        return fields.map((f) => {
          if (f.masterlistKey) return employee[f.masterlistKey] ?? "";
          
          const record = balances.find(b => b.leaveTypeId === f.leaveTypeId);
          if (!record) return 0;

          return f.isTotal ? record.totalLeaveCredit : record.remainingLeaveCredit;
        });
      },
    }
  );

  // -------------------------------------------------------------------------
  // 15. EmployeeStatus
  // -------------------------------------------------------------------------
  buildMasterlistWorksheet(
    workbook,
    "EmployeeStatus",
    EMPLOYEE_CONFIG.employeeStatuses,
    _employees,
    {
      prependFields: MASTERLIST_PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        const source = employee.employeeStatuses;
        return fields.map((f) => {
          if (f.masterlistKey) return employee[f.masterlistKey] ?? "";
          return source?.[f.key] ?? "";
        });
      },
    }
  );

  // -------------------------------------------------------------------------
  // Finalize and trigger download
  // -------------------------------------------------------------------------
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, "Masterlist.xlsx");
};